import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PermissionGate } from '@/components/shared/permission-gate';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { SwitchField } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { PERMISSIONS } from '@/lib/permissions';
import { uploadBlob } from '@/lib/upload';
import { ContentPageHeader } from '@/pages/content/content-page-header';
import { useContentPeriodStore } from '@/pages/content/content-period-store';
import { formatDateTime } from '@/utils/format';

interface Announcement {
  id: string;
  title: string;
  body: string;
  imageKey: string | null;
  imageUrl: string | null;
  publishAt: string | null;
  expiresAt: string | null;
  pinned: boolean;
  status: 'draft' | 'scheduled' | 'published';
  publishedAt: string | null;
  canDelete?: boolean;
}

const publishDisplay = (row: Announcement): string | null => row.publishAt ?? row.publishedAt;

const announcementStatusVariant = (status: Announcement['status']) => {
  if (status === 'published') return 'success' as const;
  if (status === 'scheduled') return 'info' as const;
  return 'neutral' as const;
};

const saveToastKey = (
  status: Announcement['status'],
  wasPublished: boolean,
): 'announcements.published' | 'announcements.scheduled' | 'announcements.saved' => {
  if (status === 'published' && !wasPublished) return 'announcements.published';
  if (status === 'scheduled') return 'announcements.scheduled';
  return 'announcements.saved';
};

type PublishTiming = 'draft' | 'now' | 'schedule';

const emptyForm = () => ({
  title: '',
  body: '',
  imageKey: null as string | null,
  imagePreviewUrl: null as string | null,
  publishTiming: 'draft' as PublishTiming,
  publishAt: null as string | null,
  expiresAt: null as string | null,
  pinned: false,
});

const AnnouncementPreview = ({
  announcement,
  imageAlt,
  pinnedLabel,
}: {
  announcement: Announcement;
  imageAlt: string;
  pinnedLabel: string;
}) => (
  <div className="overflow-hidden rounded-lg border border-border bg-surface">
    {announcement.imageUrl ? (
      <img
        src={announcement.imageUrl}
        alt={imageAlt}
        className="max-h-64 w-full object-cover"
      />
    ) : null}
    <div className="space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-card-title font-semibold text-fg">{announcement.title}</p>
        {announcement.pinned ? (
          <Badge variant="info">{pinnedLabel}</Badge>
        ) : null}
      </div>
      <p className="whitespace-pre-wrap text-body-sm text-fg">{announcement.body}</p>
    </div>
  </div>
);

export const AnnouncementsPage = () => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [detail, setDetail] = useState<Announcement | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [publishTarget, setPublishTarget] = useState<Announcement | null>(null);
  const [unpublishTarget, setUnpublishTarget] = useState<Announcement | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Announcement | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const dateRange = useContentPeriodStore((state) => state.dateRange);

  const list = useQuery({
    queryKey: ['content', 'announcements', dateRange.from, dateRange.to],
    queryFn: () =>
      api.get<{ data: Announcement[] }>('/content/announcements', {
        query: {
          dateFrom: dateRange.from ?? undefined,
          dateTo: dateRange.to ?? undefined,
        },
      }),
  });

  const resetForm = () => {
    setForm(emptyForm());
    setEditing(null);
  };

  const openCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEdit = (announcement: Announcement) => {
    setEditing(announcement);
    setForm({
      title: announcement.title,
      body: announcement.body,
      imageKey: announcement.imageKey,
      imagePreviewUrl: announcement.imageUrl,
      publishTiming: announcement.status === 'scheduled' ? 'schedule' : 'draft',
      publishAt: announcement.publishAt,
      expiresAt: announcement.expiresAt,
      pinned: announcement.pinned,
    });
    setDetail(null);
    setFormOpen(true);
  };

  const editingPublished = editing?.status === 'published';

  const scheduleInPast =
    form.publishTiming === 'schedule' &&
    form.publishAt !== null &&
    new Date(form.publishAt).getTime() <= Date.now();

  const publishReferenceAt = (() => {
    if (editingPublished) return editing?.publishAt ?? editing?.publishedAt ?? null;
    if (form.publishTiming === 'schedule') return form.publishAt;
    if (form.publishTiming === 'now') return new Date().toISOString();
    return null;
  })();

  const expiryBeforePublish =
    form.expiresAt !== null &&
    publishReferenceAt !== null &&
    new Date(form.expiresAt).getTime() <= new Date(publishReferenceAt).getTime();

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        title: form.title.trim(),
        body: form.body.trim(),
        imageKey: form.imageKey,
        expiresAt: form.expiresAt,
        pinned: form.pinned,
        ...(editingPublished
          ? {}
          : { publishAt: form.publishTiming === 'schedule' ? form.publishAt : null }),
      };

      if (editing) {
        const updated = await api.patch<Announcement>(
          `/content/announcements/${editing.id}`,
          body,
        );
        if (
          !editingPublished &&
          form.publishTiming === 'now' &&
          updated.status !== 'published'
        ) {
          return api.post<Announcement>(`/content/announcements/${updated.id}/publish`);
        }
        return updated;
      }

      const created = await api.post<Announcement>('/content/announcements', body);
      if (form.publishTiming === 'now') {
        return api.post<Announcement>(`/content/announcements/${created.id}/publish`);
      }
      return created;
    },
    onSuccess: (announcement) => {
      toast.success(t(saveToastKey(announcement.status, editing?.status === 'published')));
      setFormOpen(false);
      resetForm();
      setDetail(announcement);
      void queryClient.invalidateQueries({ queryKey: ['content', 'announcements'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const publish = useMutation({
    mutationFn: (id: string) => api.post<Announcement>(`/content/announcements/${id}/publish`),
    onSuccess: (announcement) => {
      toast.success(t('announcements.published'));
      setPublishTarget(null);
      setDetail((current) => (current?.id === announcement.id ? announcement : current));
      void queryClient.invalidateQueries({ queryKey: ['content', 'announcements'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const unpublish = useMutation({
    mutationFn: (id: string) => api.post<Announcement>(`/content/announcements/${id}/unpublish`),
    onSuccess: (announcement) => {
      toast.success(t('announcements.unpublished'));
      setUnpublishTarget(null);
      setDetail((current) => (current?.id === announcement.id ? announcement : current));
      void queryClient.invalidateQueries({ queryKey: ['content', 'announcements'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/content/announcements/${id}`),
    onSuccess: () => {
      toast.success(t('announcements.deleted'));
      setDeleteTarget(null);
      setDetail(null);
      void queryClient.invalidateQueries({ queryKey: ['content', 'announcements'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<Announcement>[] = [
    {
      id: 'publishAt',
      header: t('announcements.columns.publishAt'),
      cell: (row) => {
        const value = publishDisplay(row);
        return value ? formatDateTime(value) : '—';
      },
    },
    {
      id: 'title',
      header: t('announcements.columns.title'),
      cell: (row) => row.title,
    },
    {
      id: 'expiresAt',
      header: t('announcements.columns.expiresAt'),
      cell: (row) => (row.expiresAt ? formatDateTime(row.expiresAt) : '—'),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('announcements.columns.status'),
      cell: (row) => (
        <Badge variant={announcementStatusVariant(row.status)}>{t(`status.${row.status}`)}</Badge>
      ),
    },
  ];

  const rows = list.data?.data ?? [];
  const canSave =
    form.title.trim().length > 0 &&
    form.body.trim().length > 0 &&
    !expiryBeforePublish &&
    (editingPublished ||
      (!scheduleInPast &&
        (form.publishTiming === 'draft' ||
          form.publishTiming === 'now' ||
          form.publishAt !== null)));

  const detailShowPublish = detail?.status === 'draft' || detail?.status === 'scheduled';

  return (
    <div>
      <ContentPageHeader
        actions={
          <PermissionGate permission={PERMISSIONS.CONTENT_CREATE}>
            <Button onClick={openCreate}>{t('announcements.create')}</Button>
          </PermissionGate>
        }
      />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('announcements.empty')}</p>}
        onRowClick={(row) => setDetail(row)}
        rowActions={(row) => (
          <div className="flex justify-end gap-2">
            {row.status === 'published' ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setUnpublishTarget(row)}
              >
                {t('announcements.unpublish')}
              </Button>
            ) : (
              <Button size="sm" onClick={() => setPublishTarget(row)}>
                {t('announcements.publish')}
              </Button>
            )}
            {row.canDelete === true ? (
              <PermissionGate permission={PERMISSIONS.CONTENT_DELETE}>
                <Button size="sm" variant="outline" onClick={() => setDeleteTarget(row)}>
                  {tCommon('action.delete')}
                </Button>
              </PermissionGate>
            ) : null}
          </div>
        )}
        actionsHeader={tCommon('table.actions')}
      />

      <Dialog
        open={detail !== null}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
      >
        <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{detail?.title ?? t('announcements.title')}</DialogTitle>
          </DialogHeader>
          {detail ? (
            <>
              <DialogBody contentClassName="flex flex-col gap-6">
                <AnnouncementPreview
                  announcement={detail}
                  imageAlt={t('announcements.imageAlt', { title: detail.title })}
                  pinnedLabel={t('announcements.pinnedYes')}
                />
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={announcementStatusVariant(detail.status)}>
                      {t(`status.${detail.status}`)}
                    </Badge>
                  </div>
                  <dl className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <dt className="text-caption text-fg-secondary">
                        {t('announcements.columns.publishAt')}
                      </dt>
                      <dd className="text-body-sm">
                        {publishDisplay(detail) ? formatDateTime(publishDisplay(detail)!) : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-caption text-fg-secondary">
                        {t('announcements.columns.expiresAt')}
                      </dt>
                      <dd className="text-body-sm">
                        {detail.expiresAt ? formatDateTime(detail.expiresAt) : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-caption text-fg-secondary">
                        {t('announcements.pinnedLabel')}
                      </dt>
                      <dd className="text-body-sm">
                        {detail.pinned ? tCommon('boolean.yes') : tCommon('boolean.no')}
                      </dd>
                    </div>
                  </dl>
                </div>
              </DialogBody>
              <DialogFooter>
                {detail.status === 'published' ? (
                  <Button variant="outline" onClick={() => setUnpublishTarget(detail)}>
                    {t('announcements.unpublish')}
                  </Button>
                ) : null}
                {detailShowPublish ? (
                  <Button variant="outline" onClick={() => setPublishTarget(detail)}>
                    {t('announcements.publish')}
                  </Button>
                ) : null}
                {detail.canDelete === true ? (
                  <PermissionGate permission={PERMISSIONS.CONTENT_DELETE}>
                    <Button variant="outline" onClick={() => setDeleteTarget(detail)}>
                      {tCommon('action.delete')}
                    </Button>
                  </PermissionGate>
                ) : null}
                <PermissionGate permission={PERMISSIONS.CONTENT_EDIT}>
                  <Button onClick={() => openEdit(detail)}>{tCommon('action.edit')}</Button>
                </PermissionGate>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={publishTarget !== null}
        onOpenChange={(next) => {
          if (!next) setPublishTarget(null);
        }}
        title={t('confirm.publishAnnouncement.title', { name: publishTarget?.title ?? '' })}
        description={t('confirm.publishAnnouncement.description')}
        confirmLabel={t('confirm.publishAnnouncement.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={publish.isPending}
        onConfirm={() => {
          if (publishTarget) publish.mutate(publishTarget.id);
        }}
      />

      <ConfirmDialog
        open={unpublishTarget !== null}
        onOpenChange={(next) => {
          if (!next) setUnpublishTarget(null);
        }}
        title={t('confirm.unpublishAnnouncement.title', { name: unpublishTarget?.title ?? '' })}
        description={t('confirm.unpublishAnnouncement.description')}
        confirmLabel={t('confirm.unpublishAnnouncement.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={unpublish.isPending}
        onConfirm={() => {
          if (unpublishTarget) unpublish.mutate(unpublishTarget.id);
        }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
        title={t('confirm.deleteAnnouncement.title', { name: deleteTarget?.title ?? '' })}
        description={t('confirm.deleteAnnouncement.description')}
        confirmLabel={t('confirm.deleteAnnouncement.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
      />

      <Dialog
        open={formOpen}
        onOpenChange={(next) => {
          setFormOpen(next);
          if (!next) resetForm();
        }}
      >
        <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>
              {editing ? t('announcements.edit') : t('announcements.create')}
            </DialogTitle>
          </DialogHeader>

          <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <div>
                <Label htmlFor="announcement-title">{t('announcements.titleLabel')}</Label>
                <Input
                  id="announcement-title"
                  value={form.title}
                  onChange={(e) => setForm((current) => ({ ...current, title: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="announcement-body">{t('announcements.bodyLabel')}</Label>
                <Textarea
                  id="announcement-body"
                  value={form.body}
                  onChange={(e) => setForm((current) => ({ ...current, body: e.target.value }))}
                  rows={5}
                />
              </div>
            </div>

            <div>
              <Label>{t('announcements.imageLabel')}</Label>
              <p className="mb-2 text-caption text-fg-secondary">{t('announcements.imageHint')}</p>
              {form.imagePreviewUrl ? (
                <div className="flex items-center gap-3">
                  <img
                    src={form.imagePreviewUrl}
                    alt=""
                    className="size-16 shrink-0 rounded-lg object-cover"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        imageKey: null,
                        imagePreviewUrl: null,
                      }))
                    }
                  >
                    {t('announcements.imageRemove')}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => imageInputRef.current?.click()}
                >
                  {t('announcements.imageAdd')}
                </Button>
              )}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (!file) return;
                  void (async () => {
                    try {
                      const { key } = await uploadBlob({ purpose: 'content_image', blob: file });
                      setForm((current) => ({
                        ...current,
                        imageKey: key,
                        imagePreviewUrl: URL.createObjectURL(file),
                      }));
                    } catch (err) {
                      toast.error(
                        err instanceof ApiError
                          ? tErrors(err.code, err.params)
                          : tErrors('UNEXPECTED'),
                      );
                    }
                  })();
                }}
              />
            </div>

            {editingPublished ? null : (
              <div className="flex flex-col gap-4">
                <div>
                  <Label>{t('announcements.whenLabel')}</Label>
                  <Listbox
                    value={form.publishTiming}
                    onChange={(value) => {
                      const next = (value as PublishTiming) ?? 'draft';
                      setForm((current) => ({
                        ...current,
                        publishTiming: next,
                        publishAt: next === 'schedule' ? current.publishAt : null,
                      }));
                    }}
                    options={[
                      { value: 'draft', label: t('announcements.whenDraft') },
                      { value: 'now', label: t('announcements.whenNow') },
                      { value: 'schedule', label: t('announcements.whenSchedule') },
                    ]}
                    popoverPortal="dialog"
                  />
                  <Tip tipId="content.announcement.when" className="mt-1" />
                </div>

                {form.publishTiming === 'schedule' ? (
                  <div>
                    <Label>{t('announcements.publishAtLabel')}</Label>
                    <DateTimePicker
                      value={form.publishAt}
                      onChange={(publishAt) => setForm((current) => ({ ...current, publishAt }))}
                      invalid={scheduleInPast}
                      popoverPortal="dialog"
                      timezoneNotice={(timezone) =>
                        t('announcements.timezoneNotice', { timezone })
                      }
                    />
                    {scheduleInPast ? (
                      <p className="mt-1 text-caption text-danger">
                        {t('announcements.publishAtFuture')}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}

            <div className="flex flex-col gap-4">
              <div>
                <Label>{t('announcements.expiresAtLabel')}</Label>
                <Tip tipId="content.announcement.expiresAt" className="mb-2" />
                <DateTimePicker
                  value={form.expiresAt}
                  onChange={(expiresAt) => setForm((current) => ({ ...current, expiresAt }))}
                  invalid={expiryBeforePublish}
                  popoverPortal="dialog"
                  timezoneNotice={(timezone) => t('announcements.timezoneNotice', { timezone })}
                />
                {expiryBeforePublish ? (
                  <p className="mt-1 text-caption text-danger">
                    {t('announcements.expiresAfterPublish')}
                  </p>
                ) : null}
              </div>

              <SwitchField
                id="announcement-pinned"
                label={t('announcements.pinnedLabel')}
                tipId="content.announcement.pinned"
                checked={form.pinned}
                onCheckedChange={(pinned) => setForm((current) => ({ ...current, pinned }))}
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {tCommon('action.cancel')}
            </Button>
            <Button
              disabled={!canSave || save.isPending}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              {!editing && form.publishTiming === 'now'
                ? t('announcements.publish')
                : tCommon('action.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
